import { defineElement, FC, onDidLoad, useElement, useListen, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { ModalWrapper } from '@atoms/modal-wrapper/modal-wrapper';
import { closeModal, modalStore, openModal } from '@source/store/modals';
import { useStylesheet } from '@utilities/hooks';

import style from './iframe-modal.scss';

interface IframeModalData {
    close?: boolean;
}

export interface IframeModal extends HTMLElement {
    id: string;
    src: string;
    modalTitle: string;
    minWidth: string;
    minHeight: string;
    classes: string;
    active: boolean;
}

const BpdIframeModal: FC<IframeModal> = ({ element }) => {
    useStylesheet(style);
    const modalWrapperContent = useElement<HTMLDivElement>('[js-hook-modal-wrapper-content]');

    const [id] = useProp<string>('id');
    const [iframeId] = useProp<string | null>('iframeId', null);
    const [, , watchActive] = useProp<boolean>('active', false);
    const [src] = useProp<string>('src', '');
    const [modalTitle] = useProp<string>('modalTitle', '');
    const [minWidth] = useProp<string>('minWidth', '100%');
    const [minHeight] = useProp<string>('minHeight', '50vh');
    const [classes] = useProp<string>('classes', '');

    let iframeCreated: boolean = false;

    onDidLoad(() => {
        if (element.active) open();

        modalStore.subscribe(
            ({ modals }) => {
                const { active } = modals[id];
                element.active = active;
            },
            ['modals'],
        );
    });

    watchActive(open);

    function open() {
        if (!iframeCreated) {
            const iframe = createIframe();
            modalWrapperContent.current?.appendChild(iframe);
            iframeCreated = true;
        } else if (element.active) {
            openModal(id);
        }
    }

    useListen(window, 'message', iframeMessageReceived);
    function iframeMessageReceived(e: MessageEvent) {
        const { data, modalId } = e.data;

        if (modalId === id) {
            runAction(data);
        }
    }

    function runAction(data: IframeModalData) {
        const closeAction = data.close;
        if (closeAction) closeModal(id);
    }

    function createIframe() {
        const iframe = document.createElement('iframe');

        iframe.src = src;
        iframe.setAttribute('style', `min-width: ${minWidth}; min-height: ${minHeight};`);

        if (iframeId) iframe.id = iframeId;
        iframe.onload = () => openModal(id);
        return iframe;
    }

    return (
        <div class={`bpd-iframe-modal__container ${classes}`}>
            <ModalWrapper id={id} active={false} title={modalTitle} />
        </div>
    );
};

BpdIframeModal.props = {
    id: {
        type: String,
        required: true,
        reflectToAttr: true,
    },
    iframeId: {
        type: String,
    },
    active: {
        type: String,
        reflectToAttr: true,
    },
    src: {
        type: String,
        reflectToAttr: true,
    },
    modalTitle: {
        type: String,
    },
    minWidth: {
        type: String,
    },
    minHeight: {
        type: String,
    },
    classes: {
        type: String,
    },
};

defineElement('bpd-iframe-modal', BpdIframeModal);
